import React, { useCallback, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

//import theme from '@ecp/common/src/style/theme/default';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { FILE_EXTENSION, FILE_SIZE } from '@ecp/common/src/components/unstyled/input/FileUploaderUnstyled';
import UploaderDropzone from '@ecp/common/src/components/file/UploaderDropzone';
import ImageCard from '@ecp/common/src/components/file/ImageCard';
import useImageFileUploader from '@ecp/common/src/components/file/useImageFileUploader';

const ImageFileUploader = React.forwardRef(
  (
    {
      guideMessages,
      imageFileList,
      setImageFileList,
      draggable,
      width,
      multiUploadMaxCount,
      showAlertDialog,
      cardProps,
      extension,
      readOnly,
      multiUpload,
      maxSize,
    },
    ref
  ) => {
    const [error, setError] = useState(false);
    const { drop, filteredImageFileList, handleDelete, handleMove, handleMultiUpload, showDropzone } =
      useImageFileUploader(
        ref,
        imageFileList,
        setImageFileList,
        multiUploadMaxCount,
        maxSize,
        showAlertDialog,
        readOnly,
        multiUpload
      );

    useImperativeHandle(ref, () => ({
      hasError: () => error,
    }));

    const handleError = useCallback(
      ({ errors }) => {
        if (errors.length > 0) {
          setError(true);
          showAlertDialog(errors[0]);
        }
      },
      [showAlertDialog]
    );

    return (
      <ImageFileUploaderContainer>
        <ImageCardsWrapper ref={drop} style={{ width }}>
          {showDropzone && (
            <UploaderDropzone
              multiple
              onChange={handleMultiUpload}
              maxCount={multiUploadMaxCount}
              extension={extension}
              maxSize={maxSize}
            />
          )}
          {filteredImageFileList.map((item, index) => (
            <ImageCard
              index={index}
              key={item.type}
              keyValue={item.type}
              value={item}
              onError={handleError}
              onDelete={(keyValue) => handleDelete(keyValue)}
              draggable={draggable}
              onMove={handleMove}
              extension={extension}
              maxSize={maxSize}
              {...cardProps}
            />
          ))}
        </ImageCardsWrapper>
        {guideMessages && (
          <>
            <Spacing top={'10px'} />
            <GuideMessagesWrapper>
              {guideMessages.map((message, index) => (
                <GuideMessageContainer key={index}>
                  <GuideMessage>{message}</GuideMessage>
                </GuideMessageContainer>
              ))}
            </GuideMessagesWrapper>
          </>
        )}
      </ImageFileUploaderContainer>
    );
  }
);

ImageFileUploader.displayName = 'ImageFileUploader';

ImageFileUploader.propTypes = {
  value: PropTypes.any,
  guideMessages: PropTypes.arrayOf(PropTypes.string),
  registeredImages: PropTypes.arrayOf(PropTypes.object),
  imageFileList: PropTypes.arrayOf(PropTypes.object),
  setImageFileList: PropTypes.func,
  draggable: PropTypes.bool,
  width: PropTypes.string,
  multiUploadMaxCount: PropTypes.number,
  showAlertDialog: PropTypes.func,
  readOnly: PropTypes.bool,
  multiUpload: PropTypes.bool,
  cardProps: PropTypes.shape({
    width: PropTypes.string,
    height: PropTypes.string,
  }),
  extension: PropTypes.arrayOf(PropTypes.string),
  maxSize: PropTypes.number,
};

ImageFileUploader.defaultProps = {
  imageFileList: [],
  guideMessage: '',
  draggable: false,
  width: 'fit-content',
  multiUploadMaxCount: 5,
  cardProps: {},
  extension: FILE_EXTENSION.IMAGE,
  maxSize: FILE_SIZE.IMAGE,
};

export default ImageFileUploader;

const ImageCardsWrapper = styled(FlexBox)`
  align-items: flex-start;
  gap: 10px;
`;

export const GuideMessage = styled.pre`
  color: ${(props) => props.theme.color.text.guide};
  font-weight: ${(props) => props.theme.font.weight.demiLight};
  font-size: ${(props) => props.theme.font.size.small};
  white-space: break-spaces;
`;

export const GuideMessageContainer = styled(FlexBox)`
  align-self: stretch;
  align-items: flex-start;
  gap: 6px;
`;

export const GuideMessagesWrapper = styled(FlexBoxColumn)`
  gap: 3px;
`;

export const ImageFileUploaderContainer = styled(FlexBoxColumn)`
  align-items: ${({ alignItems }) => alignItems};
`;

ImageFileUploaderContainer.propTypes = {
  alignItems: PropTypes.string,
};

ImageFileUploaderContainer.defaultProps = {
  alignItems: 'flex-start',
};
